/* eslint-disable react/jsx-props-no-spreading */
import { Box, Button, Fade, Flex, Image, Stack, Text } from '@chakra-ui/react';

import { UserCertificate } from '@/client/types/content/UserCertificate';
import { format } from 'date-fns';
import { useAuthStore } from '@/client/services/state/authStore';
import { useParseCertificateData } from './hooks/useParseCertificateData';
import { useTranslation } from 'react-i18next';

interface CertificateV2Props {
  certificate: UserCertificate;
  variant: 'preview' | 'modal';
  isHovered?: boolean;
}

export default function CertificateV2({
  certificate,
  variant,
  isHovered = false,
}: CertificateV2Props) {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { full_name: fullName } = user;

  const {
    url,
    displayTitle,
    hideDate,
    completedDate,
    logoHeight,
    userMetadata,
    filteredUserMetadata,
    contentMetadata,
  } = useParseCertificateData(certificate);

  // Conditional styling based on variant
  const isPreview = variant === 'preview';

  const containerStyles = isPreview
    ? {
        backgroundSize: 'contain',
        backgroundPosition: 'bottom center',
        width: 'full',
        height: { base: '300px', md: '170px', lg: '175px' },

        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
      }
    : {
        backgroundSize: { base: 'contain', sm: 'contain', md: 'cover', lg: 'cover' },
        backgroundPosition: 'center',
        width: '100%',
        height: '100%',
        maxWidth: { base: '100%', sm: '600px', md: '800px', lg: '1000px' },
        maxHeight: { base: '400px', sm: '450px', md: '650px', lg: '700px' },
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.1)',
      };

  const headerStyles = isPreview
    ? {
        paddingY: { base: 1, sm: 1, md: 1, lg: 1 },
      }
    : {
        paddingY: { base: 1, sm: 2, md: 3, lg: 4 },
      };

  const logoStyles = isPreview
    ? {
        maxWidth: { base: '12px', sm: '13px', md: '14px', lg: '15px' },
        maxHeight: { base: '12px', sm: '13px', md: '14px', lg: '15px' },
      }
    : {
        maxWidth: { base: '30px', sm: '35px', md: '55px', lg: '60px' },
        maxHeight: { base: '30px', sm: '35px', md: '55px', lg: '60px' },
      };

  const titleStyles = isPreview
    ? {
        fontSize: { base: '5px', sm: '5px', md: '6px', lg: '6px' },
      }
    : {
        fontSize: { base: '12px', sm: '14px', md: '22px', lg: '24px' },
      };

  const contentStyles = isPreview
    ? {
        marginTop: { base: 14, sm: 5, md: 6, lg: 6 },
        spacing: { base: 1, sm: 2, md: 2, lg: 2 },
      }
    : {
        marginTop: { base: 4, sm: 6, md: 11, lg: 12 },
        spacing: { base: 2, sm: 2, md: 4, lg: 4 },
      };

  const displayTitleStyles = isPreview
    ? {
        fontSize: { base: '10px', sm: '10px', md: '6px', lg: '6px' },
      }
    : {
        fontSize: { base: '12px', sm: '14px', md: '22px', lg: '24px' },
      };

  const dateStyles = isPreview
    ? {
        fontSize: { base: '6px', sm: '6px', md: '4px', lg: '4px' },
        spacing: 0,
      }
    : {
        fontSize: { base: '8px', sm: '10px', md: '15px', lg: '16px' },
        spacing: { base: 0, sm: 0, md: 1, lg: 1 },
      };

  return (
    <Box
      background="url(/public/images/CertificateBackground.png)"
      backgroundRepeat="no-repeat"
      position="relative"
      {...containerStyles}
      sx={{
        '@media print': {
          width: '11in',
          height: '8.5in',
          minWidth: '11in',
          minHeight: '8.5in',
          maxWidth: '11in',
          maxHeight: '8.5in',
          boxShadow: 'none',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '-webkit-print-color-adjust': 'exact',
          'color-adjust': 'exact',
          'print-color-adjust': 'exact',
          margin: 0,
          padding: 0,
        },
      }}
    >
      {isHovered && (
        <Fade in={isHovered} transition={{ enter: { duration: 0.3 } }}>
          <Flex
            position="absolute"
            justifyContent="center"
            alignItems="center"
            top={0}
            left={0}
            width="full"
            height="full"
            backgroundColor="rgba(0, 0, 0, 0.1)"
          >
            <Button
              backgroundColor="whiteAlpha.800"
              color="neutral.1300"
              borderRadius="full"
              width="fit-content"
              _hover={{ backgroundColor: 'neutral.300' }}
            >
              View
            </Button>
          </Flex>
        </Fade>
      )}

      <Flex
        justifyContent="space-between"
        alignItems="center"
        borderBottom="1px solid"
        borderBottomColor="neutral.100"
        width="90%"
        margin="0 auto"
        {...headerStyles}
        sx={{
          '@media print': {
            paddingY: '20px',
            borderBottomColor: 'black',
          },
        }}
      >
        <Image
          src={url}
          alt="Company Logo"
          display="inline-block"
          height={logoHeight}
          {...logoStyles}
          sx={{
            '@media print': {
              maxWidth: '80px',
              maxHeight: '80px',
            },
          }}
        />

        <Text
          fontWeight={600}
          {...titleStyles}
          sx={{
            '@media print': {
              fontSize: '28px',
              color: 'black',
            },
          }}
        >
          Certificate of Completion
        </Text>
      </Flex>

      <Stack
        marginLeft={{ base: '35%', sm: '32%', md: '34%', lg: '35%' }}
        marginRight={{ base: '20%', sm: '18%', md: '16%', lg: '15%' }}
        {...contentStyles}
        sx={{
          '@media print': {
            marginTop: '60px',
            marginLeft: '35%',
            marginRight: '15%',
            spacing: '20px',
          },
        }}
      >
        {fullName && (
          <Text
            fontWeight={700}
            fontFamily="mono"
            {...dateStyles}
            sx={{
              '@media print': {
                fontSize: '24px',
                color: 'black',
              },
            }}
          >
            {fullName}
          </Text>
        )}

        <Text
          fontWeight={700}
          fontFamily="mono"
          wordBreak="break-word"
          textAlign="left"
          {...displayTitleStyles}
          sx={{
            '@media print': {
              fontSize: '28px',
              color: 'black',
            },
          }}
        >
          {displayTitle}
        </Text>

        {!hideDate && (
          <Stack
            alignItems="flex-start"
            fontWeight={700}
            fontFamily="mono"
            {...dateStyles}
            sx={{
              '@media print': {
                fontSize: '18px',
                color: 'black',
              },
            }}
          >
            <Text>{t('learners.dashboard.achievements.certificates.date')}</Text>

            <Text>{format(new Date(completedDate), 'MMMM do, yyyy')}</Text>
          </Stack>
        )}

        {userMetadata && Object.keys(filteredUserMetadata).length > 0 && (
          <Stack spacing={0}>
            <Text fontWeight={700} fontFamily="mono" {...dateStyles}>
              {t('certificate.user_custom_fields')}
            </Text>

            {Object.keys(filteredUserMetadata).map((key) => (
              <Text key={key} {...dateStyles}>
                {key}: {filteredUserMetadata[key]}
              </Text>
            ))}
          </Stack>
        )}

        {contentMetadata && Object.keys(contentMetadata).length > 0 && (
          <Stack spacing={0}>
            <Text fontWeight={700} fontFamily="mono" {...dateStyles}>
              {t('certificate.content_custom_fields')}
            </Text>

            {Object.keys(contentMetadata).map((key) => (
              <Text key={key} {...dateStyles}>
                {key}: {contentMetadata[key]}
              </Text>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
